<template>
  <div>
    <!-- Modern hero header section with gradient -->
    <div class="business-hero-header position-relative">
      <div class="hero-overlay"></div>
      <div class="container position-relative z-index-1 py-5">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <div class="business-logo-container mr-3">
              <img :src="design.logo" alt="Business Logo" class="business-logo">
            </div>
            <div>
              <h1 class="text-white mb-1">{{design.business_name_english}}</h1>
              <div class="d-flex align-items-center">
                <span class="badge badge-light-custom mr-2">@{{design.user_name}}</span>
                <div class="business-rating">
                  <i class="fa fa-star text-warning"></i>
                  <span class="text-white">{{design.ratings ? design.ratings.toFixed(2) : '0.00'}}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="business-actions d-flex">
            <base-button @click="promotebusiness(design)" type="warning" size="sm" icon="fa fa-star" class="mr-2" v-if="!design.business_featured">
              Promote
            </base-button>
            <base-button @click="promotebusiness(design)" type="warning" size="sm" icon="fa fa-star" class="mr-2" v-if="design.business_featured">
              Demote
            </base-button>
            <base-button type="danger" size="sm" @click="deleteDesign(design)" icon="fa fa-trash">
              Delete
            </base-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content area with modern card-based layout -->
    <div class="container-fluid mt-n6">
      <div class="row">
        <!-- Left column: Business Details Card -->
        <div class="col-lg-4 mb-4">
          <!-- Use BusinessDetails component -->
          <div class="business-details-card">
            <!-- Cover Image Section -->
            <div class="business-cover-image">
              <img v-if="design.cover_image" :src="design.cover_image" alt="Cover Image" class="cover-img">
              <img v-else src="img/noimage.png" alt="No Image" class="cover-img">
            </div>

            <!-- Info Sections -->
            <div class="business-info-container">
              <!-- Category Section -->
              <div class="info-section">
                <div class="section-title">
                  <i class="ni ni-tag mr-2 text-primary"></i>
                  <span>Categories & Subcategories</span>
                </div>
                <div class="section-content">
                  <!-- Categories (English) -->
                  <div 
                    v-if="design.business_category && design.business_category.length" 
                    class="info-item"
                  >
                    <div class="info-label">Categories:</div>
                    <div class="info-value">
                      <span 
                        v-for="category in design.business_category" 
                        :key="category._id" 
                        class="badge badge-pill badge-light-primary category-item"
                      >
                        {{ category.business_category_name }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Categories (Arabic) -->
                  <div 
                    v-if="design.business_category && design.business_category.length" 
                    class="info-item"
                  >
                    <div class="info-label">Categories (AR):</div>
                    <div class="info-value">
                      <span 
                        v-for="category in design.business_category" 
                        :key="'ar-'+category._id" 
                        class="badge badge-pill badge-light-primary category-item"
                      >
                        {{ category.business_category_name_ar }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Subcategories (English) -->
                  <div 
                    v-if="design.business_subcategory && design.business_subcategory.length" 
                    class="info-item"
                  >
                    <div class="info-label">Subcategories:</div>
                    <div class="info-value">
                      <span 
                        v-for="subcategory in design.business_subcategory" 
                        :key="subcategory._id" 
                        class="badge badge-pill badge-light-info category-item"
                      >
                        {{ subcategory.business_subcategory_name }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Subcategories (Arabic) -->
                  <div 
                    v-if="design.business_subcategory && design.business_subcategory.length" 
                    class="info-item"
                  >
                    <div class="info-label">Subcategories (AR):</div>
                    <div class="info-value">
                      <span 
                        v-for="subcategory in design.business_subcategory" 
                        :key="'ar-'+subcategory._id" 
                        class="badge badge-pill badge-light-info category-item"
                      >
                        {{ subcategory.business_subcategory_name_ar }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Description Section -->
              <div class="info-section" v-if="design.business_description_english || design.business_description_arabic">
                <div class="section-title">
                  <i class="ni ni-align-left-2 mr-2 text-primary"></i>
                  <span>Description</span>
                </div>
                <div class="section-content">
                  <div v-if="design.business_description_english" class="info-item">
                    <div class="info-value description-text">{{design.business_description_english}}</div>
                  </div>
                  <div v-if="design.business_description_arabic" class="info-item mt-2">
                    <div class="info-label">Arabic:</div>
                    <div class="info-value description-text">{{design.business_description_arabic}}</div>
                  </div>
                </div>
              </div>

              <!-- Hours Section -->
              <div class="info-section">
                <div class="section-title">
                  <i class="ni ni-time-alarm mr-2 text-primary"></i>
                  <span>Hours</span>
                </div>
                <div class="section-content">
                  <div v-if="design.business_hours && design.business_hours.length" class="hours-preview">
                    <div class="d-flex mb-3">
                      <base-button @click="openbusinesshours(design.business_hours)" class="btn-sm btn-outline-primary mr-2" icon="fa fa-clock-o">
                        Business Hours ({{design.business_hours.length}})
                      </base-button>
                      <base-button @click="openleisurehours(design.leisure_hours)" class="btn-sm btn-outline-warning" icon="fa fa-coffee" v-if="design.leisure_hours && design.leisure_hours.length">
                        Break Hours ({{design.leisure_hours.length}})
                      </base-button>
                    </div>
                    
                    <!-- Quick Preview of Today's Hours -->
                    <div class="today-hours-preview" v-if="getTodayHours()">
                      <div class="small text-muted mb-1">
                        <i class="fa fa-calendar mr-1"></i>Today's Hours:
                      </div>
                      <div class="font-weight-600 text-dark">
                        {{getTodayHours().from}} - {{getTodayHours().to}}
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-hours-info">
                    <i class="ni ni-calendar-grid-58 text-muted mr-2"></i>
                    <span class="text-muted">No business hours configured</span>
                  </div>
                </div>
              </div>

              <!-- Social Links Section -->
              <div class="info-section">
                <div class="section-title">
                  <i class="ni ni-world-2 mr-2 text-primary"></i>
                  <span>Connect</span>
                </div>
                <div class="section-content">
                  <div class="d-flex flex-wrap">
                    <a @click="copylink(design._id)" id="copylink" class="social-btn invite-link mr-2 mb-2">
                      <i class="fa fa-link"></i>
                    </a>
                    <a v-if="design.fb_link && design.fb_link!=' '" target="_blank" :href="'https://www.facebook.com/'+design.fb_link" class="social-btn facebook mr-2 mb-2">
                      <i class="fa fa-facebook"></i>
                    </a>
                    <a v-if="design.twitter_link && design.twitter_link!=' '" target="_blank" :href="'https://www.twitter.com/'+design.twitter_link" class="social-btn twitter mr-2 mb-2">
                      <i class="fa fa-twitter"></i>
                    </a>
                    <a v-if="design.instagram_link && design.instagram_link!=' '" target="_blank" :href="'https://www.instagram.com/'+design.instagram_link" class="social-btn instagram mr-2 mb-2">
                      <i class="fa fa-instagram"></i>
                    </a>
                    <a v-if="design.web_link && design.web_link!=' '" target="_blank" :href="'http://'+design.web_link" class="social-btn website mr-2 mb-2">
                      <i class="fa fa-globe"></i>
                    </a>
                  </div>
                </div>
              </div>

              <!-- Contact Info Section -->
              <div class="info-section">
                <div class="section-title">
                  <i class="ni ni-mobile-button mr-2 text-primary"></i>
                  <span>Contact Information</span>
                </div>
                <div class="section-content">
                  <div v-if="design.phone && design.phone!=' '" class="info-item">
                    <div class="info-label">Phone:</div>
                    <div class="info-value">{{design.phone}}</div>
                  </div>
                  <div v-if="design.email && design.email!=' '" class="info-item">
                    <div class="info-label">Email:</div>
                    <div class="info-value">{{design.email}}</div>
                  </div>
                  <div v-if="design.address && design.address!=' '" class="info-item">
                    <div class="info-label">Address:</div>
                    <div class="info-value">{{design.address}}</div>
                  </div>
                  <div v-if="design.address_ar && design.address_ar!=' '" class="info-item">
                    <div class="info-label">Address (AR):</div>
                    <div class="info-value">{{design.address_ar}}</div>
                  </div>
                  <div v-if="design.location_lat && design.location_long" class="info-item">
                    <a 
                      :href="'https://www.google.com/maps/search/?api=1&query='+design.location_lat+','+design.location_long" 
                      target="_blank"
                      class="btn btn-sm btn-outline-info btn-block"
                    >
                      <i class="ni ni-pin-3 mr-1"></i> View on Map
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Right column: Content Cards -->
        <div class="col-lg-8">
          
          <!-- Hashtag Management Card -->
     <div class="content-card mb-4">
<HashtagManagement 
  :businessId="$route.params.id || id" 
  :initialHashtagLimit="design.max_hashtags || 5"
  :businessDetails="design"
  @limit-updated="updateBusinessHashtagLimit"
/>
</div>
          
          <!-- Branches Card -->
          <div class="content-card mb-4">
            <div class="branch-locations-header">
              <div class="d-flex justify-content-between align-items-center">
                <div class="branch-title-section" @click="toggleBranchesView" style="cursor: pointer; flex: 1;">
                  <h5 class="m-0">
                    Branch Locations
                    <span v-if="branches && branches.length" class="badge badge-info badge-pill ml-2">
                      {{ branches.length }}
                    </span>
                  </h5>
                </div>
                <div class="branch-controls" @click.stop>
                  <button @click="addNewBranch()" class="btn btn-sm btn-primary mr-2">
                    <i class="fa fa-plus"></i> Add Branch
                  </button>
                  <div @click="toggleBranchesView" style="cursor: pointer; padding: 5px; display: inline-block;">
                    <i class="fa" :class="showBranches ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="card-body p-0" v-if="showBranches">
              <div v-if="branches && branches.length" class="branch-list">
                <!-- Branch cards start -->
                <div 
                  v-for="branch in branches" 
                  :key="branch._id" 
                  class="branch-item"
                  :class="{'main-branch-item': branch.is_main}"
                >
                  <div class="branch-item-content">
                    <div class="branch-header">
                      <h5 class="mb-0">{{ branch.name }}</h5>
                      <span v-if="branch.is_main" class="main-branch-badge">
                        <i class="fa fa-star mr-1"></i> MAIN BRANCH
                      </span>
                    </div>
                    
                    <div class="branch-description">
                      <div v-if="branch.name_ar" class="branch-ar-name">{{ branch.name_ar }}</div>
                      <div v-if="branch.address" class="branch-address">
                        <i class="ni ni-pin-3 text-danger"></i> {{ branch.address }}
                      </div>
                      <div v-if="branch.address_ar" class="branch-address-ar">{{ branch.address_ar }}</div>
                    </div>

                    <div class="branch-actions">
                      <button @click="viewBranchDetails(branch)" class="btn btn-sm btn-info">
                        <i class="fa fa-eye"></i> Details
                      </button>
                      <button @click="editBranch(branch)" class="btn btn-sm btn-primary">
                        <i class="fa fa-pencil"></i> Edit
                      </button>
                      <button 
                        v-if="!branch.is_main" 
                        @click="setAsMainBranch(branch)" 
                        class="btn btn-sm btn-success"
                      >
                        <i class="fa fa-star"></i> Set Main
                      </button>
                      <button @click="deleteBranch(branch)" class="btn btn-sm btn-danger">
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <!-- Branch cards end -->
              </div>
                <div v-else class="no-branches text-center p-4">
                <i class="ni ni-building text-muted" style="font-size: 3rem;"></i>
                <h5 class="mt-3">No Branches Added Yet</h5>
                <p class="text-muted">Create branches to manage multiple locations for this business</p>
              </div>
            </div>
          </div>

          <!-- Customer Ratings & Reviews Card -->
          <div class="content-card mb-4">
            <div class="customer-ratings-header">
              <h5 class="m-0">Customer Ratings & Reviews</h5>
            </div>
            
            <div class="card-body">
              <div v-if="commentsdata && commentsdata.length > 0" class="reviews-list">
                <div v-for="(comment, index) in commentsdata" :key="index" class="review-item">
                  <div class="review-header">
                    <div class="reviewer-info">
                      <div class="reviewer-avatar">
                        <img :src="comment.user_pic || 'img/icons/default-avatar.png'" alt="User Avatar">
                      </div>
                      <div class="reviewer-details">
                        <h6 class="mb-0">{{ comment.user_name }}</h6>
                        <div class="review-date">{{ formatDate(comment.create_date) }}</div>
                      </div>
                    </div>
                    <div class="review-rating">
                      <i class="fa fa-star text-warning"></i> 
                      <span>{{ comment.rating.toFixed(1) }}</span>
                    </div>
                  </div>
                  <div class="review-content">
                    <p>{{ comment.review }}</p>
                  </div>
                </div>
              </div>
              <div v-else class="no-reviews text-center p-4">
                <i class="ni ni-chat-round text-muted" style="font-size: 3rem;"></i>
                <h5 class="mt-3">No reviews yet for this business</h5>
                <p class="text-muted">Reviews will appear here when customers rate this business</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- No item found display -->
    <div v-if="deleteitem" class="container mt-5">
      <div class="row justify-content-center">
        <div class="col-md-8">
          <div class="not-found-card text-center">
            <img src="img/dashboard_no_item.jpg" alt="No item found" class="not-found-img mb-4" />
            <h3 class="mb-3">Item Not Found</h3>
            <p class="text-muted mb-4">This item has been deleted or does not exist.</p>
            <router-link to="/businesses" class="btn btn-primary">
              <i class="fa fa-arrow-left mr-2" /> Back to Businesses
            </router-link>
          </div>
        </div>
      </div>    
    </div>
    
    <!-- MODALS -->
    <!-- Business Hours Modal -->
    <hours-modal
      :show.sync="businesshours"
      :hours-data="businesshoursdata"
      title="Business Hours"
      type="business"
    />
    
    <!-- Break Hours Modal -->
    <hours-modal
      :show.sync="leisurehours"
      :hours-data="leisurehoursdata"
      title="Break Hours"
      type="leisure"
    />
    
    <!-- Branch Details Modal -->
    <branch-details-modal
      :show.sync="branchDetailsModal"
      :branch="currentBranch"
      @open-hours="openBranchBusinessHours"
      @open-leisure-hours="openBranchLeisureHours"
      @open-maps="openMaps"
    />
    
    <!-- Branch Add/Edit Modal -->
    <branch-modal
      :show.sync="branchModal"
      :branch="currentBranch"
      :saving="savingBranch"
      @save="saveBranch"
    />
  </div>
</template>

<script>
  import axios from "axios";
  import VueSwal from 'vue-swal';
  import BaseInput from "@/components/BaseInput";
  import BaseButton from "@/components/BaseButton";
  import Modal from "@/components/Modal";
  import Multiselect from 'vue-multiselect';
  import 'vue-multiselect/dist/vue-multiselect.min.css';
  
  // Import custom business components
  import BusinessDetails from "@/components/business/BusinessDetails";
  import BranchesList from "@/components/business/BranchesList";
  import BranchModal from "@/components/business/BranchModal";
  import BranchDetailsModal from "@/components/business/BranchDetailsModal";
  import HoursModal from "@/components/business/HoursModal";

  import HashtagManagement from "@/components/HashtagManagement";
  
  export default {
    name: 'ViewBusiness',
    components: {
      // eslint-disable-next-line vue/no-unused-components
      BaseInput,
      BaseButton,
      Modal,
      Multiselect,
      BusinessDetails,
      BranchesList,
      BranchModal,
      BranchDetailsModal,
      HoursModal,
      HashtagManagement
    },
    props: ['id'],
    data() {
      return {
        design: {},
        commentsdata: [],
        deleteitem: false,
        businesshours: false,
        leisurehours: false,
        businesshoursdata: [],
        leisurehoursdata: [],
        // Branch management data
        branches: [],
        showBranches: false,
        branchModal: false,
        branchDetailsModal: false,
        currentBranch: null,
        savingBranch: false,
        // Hashtag management data
        hashtagLimit: 5,
        showHashtags: false,
        businessHashtags: [],
        hashtagSearch: '',
        isSavingHashtagLimit: false,
        isTogglingStatus: false,
        // Original data props
        auth: {
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': 'nabnee#' + localStorage.getItem('accessToken'),
            '_id': localStorage.getItem('_id')
          }
        },
        auth1: { 
          headers: { 
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': 'nabnee#' + localStorage.getItem('accessToken'),
            '_id': localStorage.getItem('_id')
          }
        },
        statuses: {
         1: {id: 'pending', val: 'pending'},
         2: {id: 'approved', val: 'approved'}
        },
        model: {option_type: 'text'},
        message: '',
        options: '',
        path: '',
        test_type: [{nature: '', amount: ''}],
      };
    },
    computed: {
      filteredSubcategories() {
        if (!this.model.business_category || this.model.business_category.length === 0) {
          return [];
        }
        
        // Get the IDs of selected categories
        const categoryIds = this.model.business_category.map(cat => cat._id);
        
        // Filter subcategories that belong to any of the selected categories
        return this.subcategories.filter(subcat => 
          categoryIds.includes(subcat.business_category_oid)
        );
      },
      filteredHashtags() {
        return this.businessHashtags.filter(hashtag => {
          return hashtag.hashtag_text.toLowerCase().includes(this.hashtagSearch.toLowerCase());
        });
      },
      mostUsedHashtag() {
        if (!this.businessHashtags || this.businessHashtags.length === 0) {
          return null;
        }
        
        // Find the hashtag with the highest usage count
        return this.businessHashtags.reduce((prev, current) => {
          return (prev.usage_count > current.usage_count) ? prev : current;
        });
      }
    },
    watch: {
      branchModal(newVal, oldVal) {
      }
    },
    mounted() {
      this.path = process.env.VUE_APP_API_ENDPOINT;
      
      // Single source of truth for business details
      this.getDesignDetails();
      
      // Check if we should fetch branches right away
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('tab') === 'branches') {
        this.showBranches = true;
        this.fetchBranches();
      }
    },
    methods: {
      formatDate(dateString) {
        if (!dateString) return '';
        
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        } catch (e) {
          console.error("Error formatting date:", e);
          return dateString;
        }
      },
      updateBusinessHashtagLimit(newLimit) {
  this.design.max_hashtags = newLimit;
},
      
      getDesignDetails() {
        const businessId = this.$route.params.id || this.id;
        console.log("Fetching business details for ID:", businessId);
        
        axios.post(process.env.VUE_APP_API_ENDPOINT+'read_business_by_id', {user_id: businessId}, this.auth)
          .then(response => {
            console.log("Business details API response:", response);
            console.log("Business details data:", response.data);
            
            if (response.data.details && response.data.details.length > 0) {
              // Defensive: ensure max_hashtags is a number
              const businessObj = response.data.details[0];
              if (businessObj.max_hashtags) {
                businessObj.max_hashtags = Number(businessObj.max_hashtags);
              }
              this.design = businessObj;
              this.getComments();
              
              // If branches are in the response, set them
              if (this.design.branches && this.design.branches.length > 0) {
                this.branches = this.design.branches;
              }
            } else {
              this.deleteitem = true;
            }
          })
          .catch(error => {
            console.error("Error fetching business details:", error);
            this.deleteitem = true;
          });
      },
      
      getComments() {
        axios.post(process.env.VUE_APP_API_ENDPOINT+'read_business_comments', {user_id: this.$route.params.id || this.id}, this.auth)
          .then(response => {
            console.log("Comments:", response);
            if (response.data.comments) {
              this.commentsdata = response.data.comments;
            }
          })
          .catch(error => {
            console.error("Error fetching comments:", error);
          });
      },
      
      copylink(id) {
        var link = window.location.origin + '/#/businessdetails/' + id;
        var $temp = $("<input>");
        $("body").append($temp);
        $temp.val(link).select();
        document.execCommand("copy");
        $temp.remove();
        swal("Copied!", "The invite link has been copied to clipboard.", "success");
      },
      
      getTodayHours() {
        if (!this.design.business_hours || !this.design.business_hours.length) {
          return null;
        }
        
        // Get today's day name
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const today = days[new Date().getDay()];
        
        // Find today's hours if they exist
        const todayHours = this.design.business_hours.find(h => h.day === today);
        return todayHours || null;
      },
      
      openbusinesshours(business_hours) {
        this.businesshoursdata = [];
        if (business_hours) {
          try {
            // Check if business_hours is already an array or needs parsing
            if (Array.isArray(business_hours)) {
              this.businesshoursdata = business_hours;
            } else if (typeof business_hours === 'string') {
              this.businesshoursdata = JSON.parse(business_hours);
            }
            console.log("Business hours data loaded:", this.businesshoursdata);
          } catch (e) {
            console.error("Error processing business hours:", e);
          }
        }
        this.businesshours = true;
      },
      
      openleisurehours(leisure_hours) {
        this.leisurehoursdata = [];
        if (leisure_hours) {
          try {
            // Check if leisure_hours is already an array or needs parsing
            if (Array.isArray(leisure_hours)) {
              this.leisurehoursdata = leisure_hours;
            } else if (typeof leisure_hours === 'string') {
              this.leisurehoursdata = JSON.parse(leisure_hours);
            }
            console.log("Leisure hours data loaded:", this.leisurehoursdata);
          } catch (e) {
            console.error("Error processing leisure hours:", e);
          }
        }
        this.leisurehours = true;
      },
      
      // Branch management methods
      toggleBranchesView() {
        console.log("=== TOGGLE BRANCHES VIEW CALLED ===");
        console.log("Event target:", event.target);
        console.log("Current showBranches state:", this.showBranches);
        console.log("Current branches:", this.branches);
        
        this.showBranches = !this.showBranches;
        
        // Fetch branches if toggling open and we don't have them yet
        if (this.showBranches && this.branches.length === 0) {
          console.log("Fetching branches because showBranches is true and no branches loaded");
          this.fetchBranches();
        }
        
        console.log("After toggle, showBranches is now:", this.showBranches);
      },
      
      fetchBranches(forceRefresh = false) {
        // Check if business details are loaded
        if (!this.design || !this.design._id) {
          console.log("Business details not loaded yet, cannot fetch branches");
          return;
        }
        
        // If not forcing refresh, first check if we already have branches from the main business data
        if (!forceRefresh && this.design && this.design.branches && this.design.branches.length > 0) {
          console.log("Using branches from business data:", this.design.branches);
          this.branches = this.design.branches;
          return;
        }
        
        // Make API call to get fresh branch data
        console.log("Making API call to get fresh branches...");
        axios
          .post(
            process.env.VUE_APP_API_ENDPOINT + 'get_branches',
            { business_id: this.design._id },
            this.auth
          )
          .then(response => {
            console.log("Branches API response:", response);
            if (response.data.success && response.data.details) {
              this.branches = response.data.details.branches || [];
              console.log("Fetched branches from API:", this.branches);
              
              // Update the design object's branches too for consistency
              if (this.design) {
                this.design.branches = this.branches;
              }
            } else {
              console.error("Error fetching branches:", response.data.message);
              this.branches = [];
            }
          })
          .catch(error => {
            console.error("Error fetching branches:", error);
            this.branches = [];
          });
      },
      
      viewBranchDetails(branch) {
        this.currentBranch = branch;
        this.branchDetailsModal = true;
      },
      
      addNewBranch() {
        console.log("=== ADD NEW BRANCH CALLED ===");
        console.log("Current branchModal state:", this.branchModal);
        console.log("Event target:", event.target);
        console.log("Current branch:", this.currentBranch);
        
        // Reset the current branch
        this.currentBranch = null;
        this.branchModal = true;
        
        console.log("After setting branchModal to true:", this.branchModal);
        console.log("After resetting currentBranch to null:", this.currentBranch);
      },
      
      editBranch(branch) {
        console.log("=== EDIT BRANCH CALLED ===");
        console.log("Branch to edit:", branch);
        
        // Check if business details are loaded
        if (!this.design || !this.design._id) {
          swal({
            title: 'Error',
            text: 'Business details are not loaded yet. Please wait and try again.',
            icon: 'error',
          });
          return;
        }
        
        // Set the current branch for reference and open modal
        this.currentBranch = branch;
        this.branchModal = true;
      },
      
      openMaps(branch) {
        if (branch.location_lat && branch.location_long) {
          const url = `https://www.google.com/maps/search/?api=1&query=${branch.location_lat},${branch.location_long}`;
          window.open(url, '_blank');
        }
      },
      
      // Open Branch business/leisure hours in modal
      openBranchBusinessHours(branch) {
        if (branch && branch.business_hours) {
          this.openbusinesshours(branch.business_hours);
        } else {
          this.openbusinesshours([]);
        }
      },
      
      openBranchLeisureHours(branch) {
        if (branch && branch.leisure_hours) {
          this.openleisurehours(branch.leisure_hours);
        } else {
          this.openleisurehours([]);
        }
      },
      
      saveBranch(branchData) {
        console.log("Save branch called with data:", branchData);
        
        // Check if business details are loaded
        if (!this.design || !this.design._id) {
          console.error("Business details not loaded or missing _id");
          swal({
            title: 'Error',
            text: 'Business details are not loaded yet. Please wait and try again.',
            icon: 'error',
          });
          return;
        }

        // Determine if this is an add or update operation
        const isUpdate = this.currentBranch && this.currentBranch._id;
        const endpoint = isUpdate ? 'update_branch' : 'add_branch';
        
        const data = {
          business_id: this.design._id,
          branch: branchData
        };
        
        if (isUpdate) {
          data.branch_id = this.currentBranch._id;
        }
        
        console.log("=== DATA BEING SENT TO SERVER ===");
        console.log("Endpoint:", endpoint);
        console.log("business_id:", data.business_id);
        console.log("Full data object:", JSON.stringify(data, null, 2));
        
        // Set loading state
        this.savingBranch = true;
        
        axios
          .post(
            process.env.VUE_APP_API_ENDPOINT + endpoint,
            data,
            this.auth
          )
          .then(response => {
            console.log("=== SERVER RESPONSE ===");
            console.log("Full response object:", response);
            console.log("Response data:", response.data);
            console.log("Response status:", response.status);
            
            this.savingBranch = false;
            
            if (response.data.success) {
              console.log("SUCCESS: Branch operation completed");
              
              swal({
                title: isUpdate ? 'Updated!' : 'Created!',
                text: isUpdate 
                  ? 'Branch has been updated successfully!' 
                  : 'New branch has been added successfully!',
                icon: 'success',
              }).then(() => {
                this.branchModal = false;
                
                // Force refresh branches to get the real data from server
                this.fetchBranches(true);
                
                // If setting as main branch, refresh business details too
                if (data.branch.is_main) {
                  this.refreshBusinessDetails();
                }
              });
            } else {
              console.error("ERROR: Server returned success=false");
              console.error("Error message:", response.data.message);
              
              swal({
                title: 'Error',
                text: response.data.message || 'Failed to save branch',
                icon: 'error',
              });
            }
          })
          .catch(error => {
            console.error("=== AXIOS ERROR ===");
            console.error("Error object:", error);
            
            this.savingBranch = false;
            
            swal({
              title: 'Error',
              text: 'An unexpected error occurred. Please try again.',
              icon: 'error',
            });
          });
      },
      
      setAsMainBranch(branch) {
        // Check if business details are loaded
        if (!this.design || !this.design._id) {
          swal({
            title: 'Error',
            text: 'Business details are not loaded yet. Please wait and try again.',
            icon: 'error',
          });
          return;
        }

        swal({
          title: 'Set as Main Branch?',
          text: "This will update business address and hours to match this branch.",
          icon: 'warning',
          buttons: {cancel: true, confirm: true},
        }).then((result) => {
          if (result) {
            const data = {
              business_id: this.design._id,
              branch_id: branch._id,
              branch: {
                ...branch,
                is_main: true
              }
            };
            
            axios
              .post(
                process.env.VUE_APP_API_ENDPOINT + 'update_branch',
                data,
                this.auth
              )
              .then(response => {
                console.log("Branch updated:", response);
                if (response.data.success) {
                  swal({
                    title: 'Updated!',
                    text: 'Main branch has been updated successfully!',
                    icon: 'success',
                  }).then(() => {
                    // Refresh branches and business details
                    this.fetchBranches(true);
                    this.refreshBusinessDetails();
                  });
                } else {
                  swal({
                    title: 'Error',
                    text: response.data.message || 'Failed to update branch',
                    icon: 'error',
                  });
                }
              })
              .catch(error => {
                console.error("Error updating branch:", error);
                swal({
                  title: 'Error',
                  text: 'An unexpected error occurred. Please try again.',
                  icon: 'error',
                });
              });
          }
        });
      },
      
      deleteBranch(branch) {
        // Check if business details are loaded
        if (!this.design || !this.design._id) {
          swal({
            title: 'Error',
            text: 'Business details are not loaded yet. Please wait and try again.',
            icon: 'error',
          });
          return;
        }

        swal({
          title: 'Delete Branch?',
          text: "This action cannot be undone.",
          icon: 'warning',
          buttons: {cancel: true, confirm: true},
          dangerMode: true,
        }).then((result) => {
          if (result) {
            axios
              .post(
                process.env.VUE_APP_API_ENDPOINT + 'delete_branch',
                {
                  business_id: this.design._id,
                  branch_id: branch._id
                },
                this.auth
              )
              .then(response => {
                console.log("Branch deleted:", response);
                if (response.data.success) {
                  swal({
                    title: 'Deleted!',
                    text: 'Branch has been deleted successfully!',
                    icon: 'success',
                  }).then(() => {
                    this.fetchBranches(true); // Force refresh the branches list
                    // Refresh business details to ensure consistency
                    this.refreshBusinessDetails();
                  });
                } else {
                  swal({
                    title: 'Error',
                    text: response.data.message || 'Failed to delete branch',
                    icon: 'error',
                  });
                }
              })
              .catch(error => {
                console.error("Error deleting branch:", error);
                swal({
                  title: 'Error',
                  text: 'An unexpected error occurred. Please try again.',
                  icon: 'error',
                });
              });
          }
        });
      },
      
      // Hashtag management methods
      toggleHashtagsView() {
        this.showHashtags = !this.showHashtags;
        
        // Fetch hashtags if toggling open and we don't have them yet
        if (this.showHashtags && this.businessHashtags.length === 0) {
          this.fetchBusinessHashtags();
        }
      },
      
      fetchBusinessHashtags() {
        if (!this.design || !this.design._id) {
          console.log("Business details not loaded yet, cannot fetch hashtags");
          return;
        }
        
        axios
          .post(
            process.env.VUE_APP_API_ENDPOINT + 'get_business_hashtags',
            { business_id: this.design._id },
            this.auth
          )
          .then(response => {
            console.log("Hashtags API response:", response);
            if (response.data.success && response.data.details) {
              this.businessHashtags = response.data.details.hashtags || [];
              console.log("Fetched hashtags from API:", this.businessHashtags);
            } else {
              console.error("Error fetching hashtags:", response.data.message);
              this.businessHashtags = [];
            }
          })
          .catch(error => {
            console.error("Error fetching hashtags:", error);
            this.businessHashtags = [];
          });
      },
      
      searchBusinessHashtags() {
        // Debounce the search input to avoid excessive API calls
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.fetchBusinessHashtags();
        }, 300);
      },
      
      updateHashtagLimit() {
        if (!this.design || !this.design._id) {
          console.error("Business details not loaded, cannot update hashtag limit");
          return;
        }
        
        const newLimit = this.hashtagLimit;
        if (newLimit < 1 || newLimit > 50) {
          return; // Limit should be between 1 and 50
        }
        
        this.isSavingHashtagLimit = true;
        
        axios
          .post(
            process.env.VUE_APP_API_ENDPOINT + 'update_business_hashtag_limit',
            {
              business_id: this.design._id,
              max_hashtags: newLimit
            },
            this.auth
          )
          .then(response => {
            this.isSavingHashtagLimit = false;
            if (response.data.success) {
              swal({
                title: 'Updated!',
                text: 'Hashtag limit has been updated successfully!',
                icon: 'success',
              }).then(() => {
                this.design.max_hashtags = newLimit;
                this.fetchBusinessHashtags(); // Refresh hashtags list
              });
            } else {
              swal({
                title: 'Error',
                text: response.data.message || 'Failed to update hashtag limit',
                icon: 'error',
              });
            }
          })
          .catch(error => {
            this.isSavingHashtagLimit = false;
            console.error("Error updating hashtag limit:", error);
            swal({
              title: 'Error',
              text: 'An unexpected error occurred. Please try again.',
              icon: 'error',
            });
          });
      },
      
      toggleHashtagStatus(hashtag) {
        if (!this.design || !this.design._id) {
          console.error("Business details not loaded, cannot toggle hashtag status");
          return;
        }

        const newStatus = hashtag.status === 'approved' ? 'blocked' : 'approved';
        this.isTogglingStatus = true;
        
        axios
          .post(
            process.env.VUE_APP_API_ENDPOINT + 'update_hashtag_status',
            {
              business_id: this.design._id,
              hashtag_id: hashtag._id,
              status: newStatus
            },
            this.auth
          )
          .then(response => {
            this.isTogglingStatus = false;
            if (response.data.success) {
              swal({
                title: 'Updated!',
                text: 'Hashtag status has been updated successfully!',
                icon: 'success',
              }).then(() => {
                // Refresh hashtags list
                this.fetchBusinessHashtags();
              });
            } else {
              swal({
                title: 'Error',
                text: response.data.message || 'Failed to update hashtag status',
                icon: 'error',
              });
            }
          })
          .catch(error => {
            this.isTogglingStatus = false;
            console.error("Error updating hashtag status:", error);
            swal({
              title: 'Error',
              text: 'An unexpected error occurred. Please try again.',
              icon: 'error',
            });
          });
      },
      
      refreshBusinessHashtags() {
        this.fetchBusinessHashtags();
      },
      
      deleteHashtag(hashtag) {
        if (!this.design || !this.design._id) {
          console.error("Business details not loaded, cannot delete hashtag");
          return;
        }

        swal({
          title: 'Delete Hashtag?',
          text: `Are you sure you want to delete #${hashtag.hashtag_text}? This action cannot be undone.`,
          icon: 'warning',
          buttons: {cancel: true, confirm: true},
          dangerMode: true,
        }).then((result) => {
          if (result) {
            axios
              .post(
                process.env.VUE_APP_API_ENDPOINT + 'delete_hashtag',
                {
                  hashtag_id: hashtag.hashtag_id
                },
                this.auth
              )
              .then(response => {
                if (response.data.success) {
                  swal({
                    title: 'Deleted!',
                    text: 'Hashtag has been deleted successfully!',
                    icon: 'success',
                  }).then(() => {
                    // Refresh hashtags list
                    this.fetchBusinessHashtags();
                  });
                } else {
                  swal({
                    title: 'Error',
                    text: response.data.message || 'Failed to delete hashtag',
                    icon: 'error',
                  });
                }
              })
              .catch(error => {
                console.error("Error deleting hashtag:", error);
                swal({
                  title: 'Error',
                  text: 'An unexpected error occurred. Please try again.',
                  icon: 'error',
                });
              });
          }
        });
      },
      
      deleteDesign(row) {
        console.log(row);
        const querystring = require('querystring');
        swal({
          title: 'Delete this item?',
          text: "This action is not reversible.",
          icon: 'warning',
          buttons: {cancel: true, confirm: true}
        }).then((result) => {
          if (result) {
            console.log('result cloud', result)
            axios.post(process.env.VUE_APP_API_ENDPOINT+'delete_business',
              querystring.stringify({ user_email:row.user_email}),
              this.auth1
            )
            .then(response => {
              console.log(response);
              swal({
                title:'Deleted!',
                text: 'Your file has been deleted.',
                icon:'success',
              }).then((result) => {
                console.log(result)
                this.deleteitem=true;
              })
            })
          }
        });
      },
      
      promotebusiness(business) {
        const querystring = require('querystring');
        const action = !business.business_featured ? 'promote' : 'demote';
        const title = !business.business_featured ? 'Promote this business?' : 'Demote this business?';
        const text = !business.business_featured ? 
          'This business will be featured on the platform.' : 
          'This business will no longer be featured on the platform.';
          
        swal({
          title: title,
          text: text,
          icon: 'warning',
          buttons: {cancel: true, confirm: true}
        }).then((result) => {
          if (result) {
            axios.post(
              process.env.VUE_APP_API_ENDPOINT + 'promote_business',
              querystring.stringify({
                action: action,
                user_id: business._id
              }),
              this.auth1
            )
            .then(response => {
              if (response.data.success) {
                swal({
                  title: !business.business_featured ? 'Promoted!' : 'Demoted!',
                  text: !business.business_featured ? 
                    'Business has been featured successfully!' : 
                    'Business has been unfeatured successfully!',
                  icon: 'success',
                }).then(() => {
                  // Update the local model
                  this.design.business_featured = !this.design.business_featured;
                });
              } else {
                swal({
                  title: 'Error',
                  text: response.data.message || 'An error occurred',
                  icon: 'error',
                });
              }
            })
            .catch(error => {
              console.error("Error:", error);
              swal({
                title: 'Error',
                text: 'An unexpected error occurred',
                icon: 'error',
              });
            });
          }
        });
      }
    }
  };
</script>

<style scoped>
/* Hero Header Section */
.business-hero-header {
  background: linear-gradient(135deg, #2dce89, #11cdef);
  min-height: 200px;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 60px;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.5));
  border-radius: 0 0 20px 20px;
  z-index: 0;
}

.business-logo-container {
  width: 90px;
  height: 90px;
  background: #fff;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.business-logo {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 50%;
}

.business-actions .base-button {
  box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.business-actions .base-button:hover {
  transform: translateY(-2px);
}

.badge-light-custom {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 10px;
  padding: 0.35rem 0.6rem;
  font-weight: 500;
}

.business-rating {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 0.25rem 0.6rem;
  display: inline-flex;
  align-items: center;
}

.business-rating i {
  margin-right: 5px;
}

.z-index-1 {
  z-index: 1;
  position: relative;
}

/* Business Details Card */
.business-details-card {
  background-color: #fff;
  border-radius: 15px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
  margin-top: -50px;
  position: relative;
  overflow: hidden;
}

.business-cover-image {
  height: 200px;
  overflow: hidden;
  position: relative;
}

.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.business-info-container {
  padding: 20px;
}

.info-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f5f5f5;
}

.info-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 15px;
  color: #344767;
  font-size: 1rem;
}

.info-item {
  margin-bottom: 8px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: 600;
  color: #8898aa;
  margin-bottom: 3px;
  font-size: 0.875rem;
}

.info-value {
  color: #344767;
}

.description-text {
  line-height: 1.6;
  color: #525f7f;
}

.badge-light-primary {
  background-color: rgba(94, 114, 228, 0.1);
  color: #5e72e4;
}

.badge-light-info {
  background-color: rgba(17, 205, 239, 0.1);
  color: #11cdef;
}

.category-item {
  margin-right: 5px;
  margin-bottom: 5px;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.35rem 0.65rem;
}

/* Hours Section */
.hours-preview {
  margin-top: 10px;
}

.today-hours-preview {
  background-color: #f8f9fe;
  padding: 10px;
  border-radius: 6px;
  border-left: 3px solid #5e72e4;
}

.font-weight-600 {
  font-weight: 600;
}

.no-hours-info {
  color: #8898aa;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  padding: 10px;
  background-color: #f8f9fe;
  border-radius: 4px;
}

/* Social Links */
.social-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  transition: all 0.2s ease;
  cursor: pointer;
}

.social-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.social-btn.invite-link {
  background-color: #5e72e4;
}

.social-btn.facebook {
  background-color: #3b5998;
}

.social-btn.twitter {
  background-color: #1da1f2;
}

.social-btn.instagram {
  background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
}

.social-btn.website {
  background-color: #4285f4;
}

/* Content Card Common Styles */
.content-card {
  background-color: #fff;
  border-radius: 15px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 30px;
}

/* Hashtag Management Section */
.hashtag-management-header {
  background: linear-gradient(to right, #2dce89, #11cdef);
  color: #fff;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.hashtag-management-header:hover {
  background: linear-gradient(to right, #26b579, #0fb8d4);
}

.hashtag-management-header .btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 0.875rem;
}

.hashtag-management-header .btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.hashtag-list {
  padding: 15px;
}

.table th, .table td {
  vertical-align: middle;
}

.table th {
  background-color: #f8f9fe;
  font-weight: 600;
}

.table td {
  border-top: 1px solid #eaeaea;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #344767;
}

/* Branch Locations Section */
.branch-locations-header {
  background: linear-gradient(to right, #2dce89, #11cdef);
  color: #fff;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.branch-locations-header:hover {
  background: linear-gradient(to right, #26b579, #0fb8d4);
}

.branch-locations-header .btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 0.875rem;
}

.branch-locations-header .btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.branch-list {
  padding: 15px;
}

.branch-item {
  border: 1px solid #eaeaea;
  border-radius: 10px;
  margin-bottom: 15px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.branch-item:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.branch-item-content {
  padding: 15px;
}

.branch-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.main-branch-item {
  border: 2px solid #2dce89;
  box-shadow: 0 5px 15px rgba(45, 206, 137, 0.2);
}

.main-branch-badge {
  background-color: #2dce89;
  color: white;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.branch-description {
  margin-bottom: 15px;
}

.branch-ar-name {
  color: #8898aa;
  font-style: italic;
  margin-bottom: 8px;
}

.branch-address {
  color: #525f7f;
  margin-bottom: 5px;
}

.branch-address-ar {
  color: #8898aa;
  direction: rtl;
}

.branch-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.no-branches {
  border: 1px dashed #d0d6e1;
  border-radius: 10px;
  margin: 15px;
}

.branch-add-button-container {
  border-top: 1px solid #eaeaea;
}

/* Customer Ratings & Reviews Section */
.customer-ratings-header {
  background: linear-gradient(to right, #2dce89, #11cdef);
  color: #fff;
  padding: 20px;
}

.reviews-list {
  padding: 0;
}

.review-item {
  padding: 20px;
  border-bottom: 1px solid #eaeaea;
}

.review-item:last-child {
  border-bottom: none;
}

.review-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.reviewer-info {
  display: flex;
  align-items: center;
}

.reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 12px;
}

.reviewer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reviewer-details h6 {
  margin: 0;
  color: #344767;
  font-weight: 600;
}

.review-date {
  font-size: 0.8rem;
  color: #8898aa;
}

.review-rating {
  background-color: #f8f9fe;
  padding: 5px 10px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  font-weight: 600;
}

.review-rating i {
  margin-right: 5px;
}

.review-content {
  color: #525f7f;
  line-height: 1.6;
}

.no-reviews {
  border: 1px dashed #d0d6e1;
  border-radius: 10px;
  margin: 15px;
  color: #8898aa;
}

/* Not Found Card */
.not-found-card {
  background-color: #fff;
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.07);
}

.not-found-img {
  max-width: 250px;
  margin: 0 auto;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .business-hero-header {
    margin-bottom: 40px;
  }
  
  .business-logo-container {
    width: 70px;
    height: 70px;
  }
  
  .business-logo {
    width: 60px;
    height: 60px;
  }
  
  h1 {
    font-size: 1.5rem;
  }
  
  .branch-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .main-branch-badge {
    margin-top: 5px;
  }
}

@media (max-width: 576px) {
  .business-hero-header {
    min-height: 160px;
  }
  
  .d-flex.align-items-center.justify-content-between {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .business-actions {
    margin-top: 15px;
    width: 100%;
    justify-content: flex-end;
  }
  
  .branch-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .branch-actions button {
    width: 100%;
  }
}
</style>
